import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Play, Pause, Check, Clock, Brain, 
  ChevronRight, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { wellbeingApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const MeditationDetailPage = () => {
  const { meditationId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [meditation, setMeditation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchMeditation();
  }, [meditationId]);

  useEffect(() => {
    let interval;
    if (isActive && !isPaused) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, isPaused]);

  const fetchMeditation = async () => {
    try {
      const data = await wellbeingApi.getMeditation(meditationId);
      setMeditation(data);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsActive(true);
    setCurrentStep(0);
    setTimer(0);
  };

  const handleNextStep = () => {
    const steps = language === 'en' ? meditation.steps_en : meditation.steps_es;
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleComplete = async (feltBetter) => {
    try {
      await wellbeingApi.completeMeditation(meditationId, {
        meditation_id: meditationId,
        duration_minutes: Math.ceil(timer / 60) || meditation.duration_minutes,
        felt_better: feltBetter
      });
      
      toast({
        title: language === 'en' ? 'Meditation Complete!' : '¡Meditación Completada!',
        description: language === 'en' 
          ? 'Great job taking time for yourself' 
          : 'Excelente trabajo cuidando de ti'
      });
      
      navigate('/wellbeing');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!meditation) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <p className="text-gray-400">
          {language === 'en' ? 'Meditation not found' : 'Meditación no encontrada'}
        </p>
      </div>
    );
  }

  const steps = language === 'en' ? meditation.steps_en : meditation.steps_es;
  const currentStepText = steps[currentStep];

  const categoryLabels = {
    breathing: language === 'en' ? 'Breathing' : 'Respiración',
    relaxation: language === 'en' ? 'Relaxation' : 'Relajación',
    focus: language === 'en' ? 'Focus' : 'Enfoque',
    sleep: language === 'en' ? 'Sleep' : 'Sueño',
    gratitude: language === 'en' ? 'Gratitude' : 'Gratitud',
    body_scan: language === 'en' ? 'Body Scan' : 'Escaneo Corporal'
  };

  // Active meditation view
  if (isActive) {
    const isLastStep = currentStep === steps.length - 1;
    
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex flex-col">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => setIsActive(false)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="text-center">
            <p className="text-gray-500 text-sm">
              {language === 'en' ? 'Step' : 'Paso'} {currentStep + 1} / {steps.length}
            </p>
            <p className="text-purple-400 font-mono text-lg">{formatTime(timer)}</p>
          </div>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="text-gray-400 hover:text-white"
          >
            {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          {/* Progress Dots */}
          <div className="flex gap-2 mb-8">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-8 bg-purple-500' 
                    : idx < currentStep 
                      ? 'bg-purple-500/50' 
                      : 'bg-gray-700'
                }`}
              />
            ))}
          </div>

          {/* Current Step */}
          <div className="max-w-md text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Brain className="w-10 h-10 text-purple-400" />
            </div>
            <p className="text-2xl text-white leading-relaxed">
              {currentStepText}
            </p>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="p-6">
          {isLastStep ? (
            <div className="space-y-4">
              <p className="text-center text-gray-400 mb-4">
                {language === 'en' ? 'Did you feel better?' : '¿Te sentiste mejor?'}
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => handleComplete(true)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  <ThumbsUp className="w-5 h-5 mr-2" />
                  {language === 'en' ? 'Yes!' : '¡Sí!'}
                </Button>
                <Button
                  onClick={() => handleComplete(false)}
                  variant="outline"
                  className="flex-1 border-gray-600"
                >
                  <ThumbsDown className="w-5 h-5 mr-2" />
                  {language === 'en' ? 'Not really' : 'No mucho'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={handleNextStep}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              {language === 'en' ? 'Next Step' : 'Siguiente Paso'}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Pre-meditation view
  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Hero Image */}
      <div className="relative h-64">
        {meditation.image_url ? (
          <img 
            src={meditation.image_url} 
            alt={meditation.title_en}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-900 to-indigo-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f1f] to-transparent" />
        
        <Link
          to="/wellbeing"
          className="absolute top-4 left-4 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
      </div>

      {/* Content */}
      <div className="px-4 -mt-16 relative z-10 pb-8">
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
          {/* Tags */}
          <div className="flex gap-2 mb-4">
            <span className="bg-purple-500/20 text-purple-400 text-xs px-3 py-1 rounded-full">
              {categoryLabels[meditation.category]}
            </span>
            <span className="bg-gray-800 text-gray-400 text-xs px-3 py-1 rounded-full">
              {meditation.difficulty}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {language === 'en' ? meditation.title_en : meditation.title_es}
          </h1>

          {/* Duration */}
          <div className="flex items-center gap-2 text-gray-400 mb-4">
            <Clock className="w-4 h-4" />
            <span>{meditation.duration_minutes} min</span>
          </div>

          {/* Description */}
          <p className="text-gray-300 mb-6">
            {language === 'en' ? meditation.description_en : meditation.description_es}
          </p>

          {/* Start Button */}
          <Button
            onClick={handleStart}
            className="w-full h-14 bg-purple-500 hover:bg-purple-600 text-lg font-semibold"
          >
            <Play className="w-6 h-6 mr-2" />
            {language === 'en' ? 'Start Meditation' : 'Comenzar Meditación'}
          </Button>
        </div>

        {/* Steps Preview */}
        <div className="mt-6 bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
          <h2 className="text-lg font-bold text-white mb-4">
            {language === 'en' ? 'Steps' : 'Pasos'} ({steps.length})
          </h2>
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <div key={idx} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-gray-400 text-sm">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeditationDetailPage;
